import { ReactNode, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  CalendarDays,
  Check,
  BarChart3,
  Home,
  Leaf,
  Loader2,
  MessageCircle,
  Pencil,
  RefreshCw,
  Settings,
  ArrowUpRight,
  UsersRound,
  WalletCards,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { TipRecord } from "@/components/TipCard";

type PractitionerType = "doctor" | "dietitian";

interface PractitionerDashboardViewProps {
  bn: boolean;
  practitionerType: PractitionerType;
  practitionerId: string;
  practitionerName: string;
  requests: any[];
  pendingRequests: any[];
  loading: boolean;
  loadError: string | null;
  processingId: string | null;
  onRequest: (id: string, action: "accepted" | "rejected") => void;
  onRetry: () => void;
}

const sameDay = (value: string) => {
  const date = new Date(value);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

export const PractitionerBottomNav = ({ practitionerType, bn }: { practitionerType: PractitionerType; bn: boolean }) => {
  const base = practitionerType === "doctor" ? "/doctor" : "/dietitian";
  const items = [
    { path: base, label: bn ? "হোম" : "Home", icon: Home, end: true },
    { path: `${base}/patients`, label: bn ? "রোগী" : "Patients", icon: UsersRound },
    { path: `${base}/inbox`, label: bn ? "ইনবক্স" : "Inbox", icon: MessageCircle },
    { path: `${base}/wallet`, label: bn ? "ওয়ালেট" : "Wallet", icon: WalletCards },
    { path: `${base}/settings`, label: bn ? "প্রোফাইল" : "Profile", icon: Settings },
  ];

  return (
    <nav className="fixed bottom-3 left-1/2 z-40 w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-full border border-[#e2ebe4] bg-white px-2 py-2 shadow-[0_8px_24px_rgba(32,63,60,0.08)] md:hidden">
      <div className="flex items-center justify-around gap-1">
        {items.map(({ path, label, icon: Icon, end }) => (
          <NavLink key={path} to={path} end={end} className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
            {({ isActive }) => (
              <>
                <span className={`rounded-xl p-2 ${isActive ? "bg-[#d8f2ed] text-[#237e76]" : "text-[#6c7b78]"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className={`text-[10px] font-semibold ${isActive ? "text-[#237e76]" : "text-[#6c7b78]"}`}>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

const PractitionerDashboardView = ({
  bn,
  practitionerType,
  practitionerId,
  practitionerName,
  requests,
  pendingRequests,
  loading,
  loadError,
  processingId,
  onRequest,
  onRetry,
}: PractitionerDashboardViewProps) => {
  const [myTips, setMyTips] = useState<Array<TipRecord & { status: "draft" | "published" }>>([]);
  const [tipsLoading, setTipsLoading] = useState(true);
  const [tipsError, setTipsError] = useState("");
  const [editingTipId, setEditingTipId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editCategory, setEditCategory] = useState("Nutrition");
  const [savingTipId, setSavingTipId] = useState<string | null>(null);
  const acceptedToday = requests.filter((request) => request.status === "accepted" && sameDay(request.created_at));
  const earningsToday = acceptedToday.reduce(
    (total, request) => total + Number(request.fee ?? request.amount ?? 0),
    0,
  );
  const publishedTipsCount = myTips.filter((tip) => tip.status === "published").length;
  const displayName = practitionerName || (practitionerType === "doctor" ? "Doctor" : "Dietitian");
  const shareTipsPath = practitionerType === "doctor" ? "/doctor/share-tips" : "/dietitian/share-tips";
  const analyticsPath = practitionerType === "doctor" ? "/doctor/analytics" : "/dietitian/analytics";

  useEffect(() => {
    if (!practitionerId) return;
    let active = true;
    const loadTips = async () => {
      setTipsLoading(true);
      setTipsError("");
      const { data, error } = await (supabase.from("health_tips" as any) as any)
        .select("id, title, body, category, image_url, created_at, status")
        .eq("author_id", practitionerId)
        .order("created_at", { ascending: false });
      if (!active) return;
      if (error) setTipsError(error.message);
      else setMyTips((data ?? []) as Array<TipRecord & { status: "draft" | "published" }>);
      setTipsLoading(false);
    };
    void loadTips();
    return () => { active = false; };
  }, [practitionerId]);

  const beginTipEdit = (tip: TipRecord) => {
    setEditingTipId(tip.id);
    setEditTitle(tip.title);
    setEditBody(tip.body || "");
    setEditCategory(tip.category || "Nutrition");
  };

  const saveTipEdit = async (tipId: string) => {
    if (!editTitle.trim() || !editBody.trim() || savingTipId) return;
    setSavingTipId(tipId);
    const { data, error } = await (supabase.from("health_tips" as any) as any)
      .update({ title: editTitle.trim(), body: editBody.trim(), category: editCategory })
      .eq("id", tipId)
      .eq("author_id", practitionerId)
      .select("id, title, body, category, image_url, created_at, status")
      .single();
    if (!error && data) {
      setMyTips((previous) => previous.map((tip) => tip.id === tipId ? data : tip));
      setEditingTipId(null);
    } else {
      setTipsError(error?.message || (bn ? "টিপ আপডেট করা যায়নি" : "Could not update tip"));
    }
    setSavingTipId(null);
  };

  return (
    <div className="-m-4 min-h-full bg-[#fbfcf8] px-4 pb-24 pt-2 text-[#203f3c] md:-m-6 md:px-6 md:pt-4">
      <div className="mx-auto max-w-md space-y-4">
        <header className="relative min-h-[132px] pt-2">
          <div className="pr-36">
            <p className="text-sm text-[#5d6f6b]">{bn ? "সুপ্রভাত" : "Good Morning,"}</p>
            <h1 className="mt-1 font-serif text-[27px] font-extrabold leading-[1.12] tracking-tight text-[#102d2a]">
              {displayName}
            </h1>
            <p className="mt-3 max-w-[235px] text-sm leading-5 text-[#5d6f6b]">
              {bn ? "আজ আপনার রোগীদের জীবনে পরিবর্তন আনুন।" : "Let’s make a difference in your patients’ lives today."}
            </p>
          </div>
          <div className="absolute right-0 top-2 h-28 w-32 overflow-hidden rounded-t-full border border-[#e2eee6] bg-[#eff7ed]">
            <img src="/sun-landscape.jpg" alt="" className="h-full w-full scale-[1.1] object-cover object-[35%_25%]" />
          </div>
        </header>

        <motion.section
          className="rounded-[18px] border border-[#e2ebe4] bg-white p-3.5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-3 flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-[#237e76]" />
              <h2 className="text-sm font-bold uppercase tracking-[0.02em] text-[#566966]">
                {bn ? "পরামর্শ অনুরোধ" : "Consultation Requests"}
              </h2>
            </div>
            {pendingRequests.length > 0 && (
              <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-[#e4f3ef] px-2 text-xs font-bold text-[#237e76]">
                {pendingRequests.length}
              </span>
            )}
          </div>

          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-[#237e76]" />
            </div>
          )}
          {!loading && loadError && (
            <div className="rounded-2xl bg-[#fff1f1] p-4 text-center">
              <p className="text-sm font-bold text-destructive">{bn ? "অনুরোধ লোড করা যায়নি" : "Could not load requests"}</p>
              <p className="mt-1 text-xs text-[#7b8986]">{loadError}</p>
              <button type="button" onClick={onRetry} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#237e76]">
                <RefreshCw className="h-3 w-3" />
                {bn ? "আবার চেষ্টা করুন" : "Try again"}
              </button>
            </div>
          )}
          {!loading && !loadError && pendingRequests.length === 0 && (
            <p className="rounded-2xl bg-[#f7fbf9] py-8 text-center text-sm text-[#7b8986]">
              {bn ? "কোন অনুরোধ নেই" : "No pending requests"}
            </p>
          )}
          <div className="space-y-2.5">
            {pendingRequests.map((request) => (
              <div key={request.id} className="rounded-[18px] border border-[#e6efea] bg-[#f7fbf9] p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e4f3ef] text-sm font-bold text-[#237e76]">
                    {(request.patient?.full_name ?? "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-[#203f3c]">{request.patient?.full_name || (bn ? "রোগী" : "Patient")}</p>
                    <p className="mt-0.5 text-xs text-[#6d7b78]">
                      {request.request_type === "follow_up" ? (bn ? "ফলো-আপ অনুরোধ" : "Follow-up request") : (request.reason ?? "")}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-[#6d7b78]">{new Date(request.created_at).toLocaleDateString()}</span>
                </div>
                {request.request_type === "follow_up" && request.message_text && (
                  <p className="mt-2 rounded-xl bg-white/80 px-3 py-2 text-xs text-[#566966]">{request.message_text}</p>
                )}
                <div className="mt-2.5 flex gap-2">
                  <button
                    type="button"
                    disabled={processingId === request.id}
                    onClick={() => onRequest(request.id, "accepted")}
                    className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full bg-[#258e7f] text-xs font-bold text-white transition-colors hover:bg-[#1f7a6d] disabled:opacity-60"
                  >
                    {processingId === request.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    {bn ? "গ্রহণ" : "Accept"}
                  </button>
                  <button
                    type="button"
                    disabled={processingId === request.id}
                    onClick={() => onRequest(request.id, "rejected")}
                    className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full border border-[#e7a7a7] text-xs font-bold text-[#d84040] transition-colors hover:bg-[#fff4f4] disabled:opacity-60"
                  >
                    <X className="h-3.5 w-3.5" />
                    {bn ? "প্রত্যাখ্যান" : "Reject"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="relative min-h-[176px] overflow-hidden rounded-[18px] border border-[#dbeae1] bg-[#f0f8ef] px-4 py-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative z-10 max-w-[57%]">
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-[#e2f1e5] px-2.5 py-1 text-[10px] font-bold text-[#236b5c]">
              <Leaf className="h-3.5 w-3.5" />
              {bn ? "দৈনিক টিপ" : "Daily tip"}
            </div>
            <h2 className="font-serif text-[19px] font-extrabold leading-6 text-[#174e49]">
              {bn ? "প্রতিদিনের স্বাস্থ্য টিপ শেয়ার করুন" : "Share a daily health tip"}
            </h2>
            <p className="mt-2 text-xs leading-5 text-[#52706f]">
              {bn ? "প্রতিদিন আপনার রোগীদের শিক্ষা দিন এবং অনুপ্রাণিত করুন।" : "Educate and inspire your patients every day."}
            </p>
            <Link to={shareTipsPath} className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#c9ddd0] bg-[#fbfcf8] px-3.5 py-2 text-[11px] font-bold text-[#174e49]">
              {bn ? "টিপ লিখুন" : "Write tip"} <Pencil className="h-3.5 w-3.5" />
            </Link>
          </div>
          <img src="/daily-tip-girl.png" alt="" className="pointer-events-none absolute -bottom-1 -right-7 h-[160px] w-[220px] object-contain object-right-bottom" />
        </motion.section>

        <motion.section
          className="rounded-[18px] border border-[#e2ebe4] bg-white p-3.5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-3 flex items-center justify-between px-1">
            <h2 className="font-serif text-base font-extrabold text-[#203f3c]">{bn ? "আজকের সারাংশ" : "Today’s summary"}</h2>
            <Leaf className="h-5 w-5 text-[#237e76]" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <SummaryTile icon={<UsersRound className="h-4 w-4" />} value={acceptedToday.length.toLocaleString()} label={bn ? "আজ দেখা রোগী" : "Patients counseled today"} />
            <SummaryTile icon={<WalletCards className="h-4 w-4" />} value={`৳${earningsToday.toLocaleString()}`} label={bn ? "আজকের আয়" : "Earnings today"} />
            <SummaryTile icon={<Leaf className="h-4 w-4" />} value={publishedTipsCount.toLocaleString()} label={bn ? "মোট শেয়ার করা টিপ" : "Total tips shared"} />
          </div>
        </motion.section>

        <motion.section
          className="rounded-[18px] border border-[#e2ebe4] bg-white p-3.5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-3 flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <Leaf className="h-5 w-5 text-[#237e76]" />
              <h2 className="font-serif text-base font-extrabold text-[#203f3c]">{bn ? "আপনার টিপ ফিড" : "Your tip feed"}</h2>
            </div>
            <span className="rounded-full bg-[#e4f3ef] px-2.5 py-1 text-[10px] font-bold text-[#237e76]">
              {publishedTipsCount} {bn ? "শেয়ার করা" : "published"}
            </span>
          </div>

          {tipsLoading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#237e76]" /></div>}
          {!tipsLoading && tipsError && <p className="rounded-xl bg-[#fff4f4] px-3 py-3 text-xs text-[#b35f5f]">{tipsError}</p>}
          {!tipsLoading && !tipsError && myTips.length === 0 && (
            <p className="rounded-2xl bg-[#f7fbf9] py-7 text-center text-sm text-[#7b8986]">
              {bn ? "আপনি এখনও কোনো টিপ শেয়ার করেননি।" : "You have not shared any tips yet."}
            </p>
          )}
          {!tipsLoading && !tipsError && myTips.length > 0 && (
            <div className="space-y-2.5">
              {myTips.map((tip) => (
                <article key={tip.id} className="rounded-2xl border border-[#e6efea] bg-[#f7fbf9] p-3">
                  {editingTipId === tip.id ? (
                    <div className="space-y-2.5">
                      <input value={editTitle} onChange={(event) => setEditTitle(event.target.value)} className="w-full rounded-xl border border-[#dbe9e4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#4b9c90]" />
                      <textarea value={editBody} onChange={(event) => setEditBody(event.target.value)} rows={4} className="w-full resize-none rounded-xl border border-[#dbe9e4] bg-white px-3 py-2.5 text-xs leading-5 outline-none focus:border-[#4b9c90]" />
                      <select value={editCategory} onChange={(event) => setEditCategory(event.target.value)} className="w-full rounded-xl border border-[#dbe9e4] bg-white px-3 py-2.5 text-xs outline-none">
                        {["Nutrition", "Lifestyle", "Mental wellness", "Fitness", "General"].map((category) => <option key={category} value={category}>{category}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => void saveTipEdit(tip.id)} disabled={savingTipId === tip.id} className="flex-1 rounded-full bg-[#258e7f] py-2 text-xs font-bold text-white disabled:opacity-60">
                          {savingTipId === tip.id ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" /> : (bn ? "সংরক্ষণ" : "Save")}
                        </button>
                        <button type="button" onClick={() => setEditingTipId(null)} className="flex-1 rounded-full border border-[#d6e8df] py-2 text-xs font-bold text-[#237e76]">{bn ? "বাতিল" : "Cancel"}</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="truncate text-sm font-bold text-[#20534f]">{tip.title}</h3>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold ${tip.status === "published" ? "bg-[#e4f3ef] text-[#237e76]" : "bg-[#fff1d9] text-[#a66a1e]"}`}>
                              {tip.status === "published" ? (bn ? "শেয়ার করা" : "Published") : (bn ? "ড্রাফট" : "Draft")}
                            </span>
                          </div>
                          <p className="mt-1 line-clamp-2 text-xs leading-5 text-[#647470]">{tip.body}</p>
                          <p className="mt-2 text-[10px] text-[#71807d]">{tip.category || (bn ? "স্বাস্থ্য" : "Health")}</p>
                        </div>
                        <button type="button" onClick={() => beginTipEdit(tip)} className="shrink-0 rounded-full bg-white p-2 text-[#237e76]" aria-label={bn ? "টিপ সম্পাদনা করুন" : "Edit tip"}>
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                      {tip.image_url && <img src={tip.image_url} alt="" className="mt-3 h-24 w-full rounded-xl object-cover" />}
                    </>
                  )}
                </article>
              ))}
            </div>
          )}
        </motion.section>

        <Link
          to={analyticsPath}
          className="flex items-center gap-3 rounded-[18px] border border-[#dbeae1] bg-white p-3.5 transition-colors hover:bg-[#f4faf7]"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e4f3ef] text-[#237e76]">
            <BarChart3 className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-[#203f3c]">{bn ? "বিশ্লেষণ দেখুন" : "View analytics"}</span>
            <span className="mt-0.5 block text-[10px] text-[#6d7b78]">
              {bn ? "আপনার পারফরম্যান্স এবং আয় দেখুন" : "Review performance and earnings"}
            </span>
          </span>
          <ArrowUpRight className="h-4 w-4 shrink-0 text-[#237e76]" />
        </Link>
      </div>
    </div>
  );
};

const SummaryTile = ({ icon, value, label }: { icon: ReactNode; value: string; label: string }) => (
  <div className="min-w-0 rounded-[15px] border border-[#edf1ed] bg-[#f8fbfa] p-2.5">
    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#e4f3ef] text-[#398b82]">{icon}</div>
    <p className="truncate text-lg font-extrabold text-[#203f3c]">{value}</p>
    <p className="mt-0.5 text-[9px] leading-3 text-[#6d7b78]">{label}</p>
  </div>
);

export default PractitionerDashboardView;